import type { Dict, Mixpanel } from "mixpanel-browser";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { MixpanelContext } from "@/hooks/useMixpanel";

let mixpanelInstance: Mixpanel | null = null;
let isInitialized = false;

function useMixpanel() {
  const [isLoading, setIsLoading] = useState(false);

  const initMixpanel = useCallback(async () => {
    if (isInitialized || mixpanelInstance) return;

    try {
      setIsLoading(true);
      const mixpanel = await import("mixpanel-browser");
      const MIXPANEL_APIKEY = import.meta.env.VITE_MIXPANEL_APIKEY;
      const REACT_APP_API_URL = import.meta.env.VITE_API_URL;

      if (MIXPANEL_APIKEY) {
        mixpanel.default.init(MIXPANEL_APIKEY, {
          debug: true,
          ignore_dnt: true,
          api_host: `${REACT_APP_API_URL}`,
        });
        mixpanelInstance = mixpanel.default;
        isInitialized = true;
      }
    } catch (error) {
      console.error("Failed to initialize Mixpanel:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const track = useCallback(
    async (eventName: string, properties?: Dict) => {
      if (!mixpanelInstance) {
        await initMixpanel();
      }
      mixpanelInstance?.track(eventName, properties);
    },
    [initMixpanel],
  );

  const identify = useCallback(
    async (userId: string) => {
      if (!mixpanelInstance) {
        await initMixpanel();
      }
      mixpanelInstance?.identify(userId);
    },
    [initMixpanel],
  );

  const setPeople = useCallback(
    async (properties: Dict) => {
      if (!mixpanelInstance) {
        await initMixpanel();
      }
      mixpanelInstance?.people.set(properties);
    },
    [initMixpanel],
  );

  useEffect(() => {
    // Initialize Mixpanel when the hook is first used
    void initMixpanel();
  }, [initMixpanel]);

  return {
    track,
    identify,
    setPeople,
    isLoading,
  };
}

interface MixpanelProviderProps {
  children: ReactNode;
}

export function MixpanelProvider({ children }: MixpanelProviderProps) {
  const mixpanel = useMixpanel();
  return <MixpanelContext value={mixpanel}>{children}</MixpanelContext>;
}
